import React from 'react';
import { 
  Box, 
  TextField, 
  Chip, 
  Typography, 
  Stack,
  Autocomplete
} from '@mui/material';
import { LocalHospital as InsuranceIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../app/store';
import { setInsurances } from '../../../../../services/CaseManagementService/policySlice';

// Predefined insurance types list
const INSURANCE_TYPES = [
  'Aetna', 'Aetna Medicare', 'Aetna Medicaid', 'Amerigroup', 'BCBS BAV', 'BCBS Blue Essential', 
  'BCBS HMO', 'BCBS Medicaid', 'BCBS Medicare', 'BCBS NWB', 'BCBS PPO', 'BCBS Trad', 'Bundled', 
  'Care N Care', 'Cigna', 'Cigna Healthspring', 'Cigna Medicare', 'Corvel', 'Endeavor', 'HD Research', 
  'Humana', 'Humana Medicare', 'LOP', 'Medicaid', 'Medicare', 'MPower', 'Multiplan', 'Multiplan Comm', 
  'Multiplan WC', 'Oscar', 'Other Commercial', 'Research Source', 'Superior Health Plan', 'Self-Pay', 
  'SWHP Comm', 'SWHP Managed Medicare', 'SWHR', 'Three Rivers', 'Tricare', 'TriWest', 'UHC', 
  'UHC Medicaid', 'UHC Medicare', 'Wellcare', 'Wellmed', 'Workers Comp'
];

const PolicyInsurancesEditor: React.FC = () => {
  const dispatch = useAppDispatch();
  const policy = useAppSelector((state) => state.policy.currentPolicy);
  
  const currentInsurances = policy?.effectiveInsurances || [];

  const handleSelectionChange = (event: any, newValues: string[]) => {
    dispatch(setInsurances(newValues));
  };

  const handleRemoveInsurance = (insuranceToRemove: string) => {
    const updatedInsurances = currentInsurances.filter(insurance => insurance !== insuranceToRemove);
    dispatch(setInsurances(updatedInsurances));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <InsuranceIcon sx={{ color: '#64748b', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Effective Insurances
        </Typography>
      </Stack>
      
      <Autocomplete
        multiple
        size="small"
        sx={{ width: 500 }}
        options={INSURANCE_TYPES}
        value={currentInsurances}
        onChange={handleSelectionChange}
        getOptionLabel={(option) => option}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search and select insurance types..."
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              sx: {
                backgroundColor: '#ffffff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e2e8f0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#059669',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#059669',
                }
              }
            }}
          />
        )}
        renderTags={() => null} // Don't render tags inline, show them below
        filterSelectedOptions
      />
      
      {currentInsurances.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {currentInsurances.map((insurance) => (
            <Chip
              key={insurance}
              label={insurance}
              onDelete={() => handleRemoveInsurance(insurance)}
              size="small"
              sx={{
                backgroundColor: '#059669',
                color: 'white',
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: '#f1f5f9'
                  }
                }
              }}
            />
          ))}
        </Box>
      )}
      
      {currentInsurances.length > 0 && (
        <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
          {currentInsurances.length} insurance type{currentInsurances.length !== 1 ? 's' : ''} selected
        </Typography>
      )}
    </Box>
  );
};

export default PolicyInsurancesEditor;
